export type SignInMethod = 'password' | 'passkey' | 'email';

const OFFER_KEY = 'asg:offerSignInSetup';
const METHOD_KEY = 'asg:lastSignInMethod';
const PASSKEY_KEY = 'asg:passkeyOnDevice';

// Browser storage can be unavailable (private modes, blocked cookies). Losing
// these hints only means the guide or remembered tab is skipped.
function attempt<T>(operation: () => T, fallback: T): T {
  try { return operation(); } catch { return fallback; }
}

/** Note a password sign-in in this tab, so the dashboard can offer guided setup. */
export function markPasswordSignIn() {
  attempt(() => window.sessionStorage.setItem(OFFER_KEY, '1'), undefined);
}

export function hasPendingSetupOffer(): boolean {
  return attempt(() => window.sessionStorage.getItem(OFFER_KEY) === '1', false);
}

export function clearSetupOffer() {
  attempt(() => window.sessionStorage.removeItem(OFFER_KEY), undefined);
}

/** Remember only the method (never the email), so shared devices reveal nothing personal. */
export function rememberSignInMethod(method: SignInMethod) {
  attempt(() => window.localStorage.setItem(METHOD_KEY, method), undefined);
}

export function lastSignInMethod(): SignInMethod | null {
  const value = attempt(() => window.localStorage.getItem(METHOD_KEY), null);
  return value === 'password' || value === 'passkey' || value === 'email' ? value : null;
}

/**
 * Whether a passkey has been added or used in this browser. The sign-in page
 * uses it to offer the passkey up front. It is only a hint: a passkey synced
 * from another device is still reachable from the first step.
 */
export function notePasskeyOnDevice(present: boolean) {
  attempt(() => window.localStorage.setItem(PASSKEY_KEY, present ? '1' : '0'), undefined);
}

/** true or false once noted in this browser; null if never noted. */
export function passkeyOnDevice(): boolean | null {
  const value = attempt(() => window.localStorage.getItem(PASSKEY_KEY), null);
  return value === '1' ? true : value === '0' ? false : null;
}
